import { tWithKeyAttr as t } from "../../../i18nHelper";
import changePasswordPopupStyles from "./changePasswordPopup.module.css";
import React, { useState } from "react";
import { IPopupWithoutProps } from "../../../types/types";
import { useFormik } from "formik";
import { changePasswordFormSchema, passwordValidationSchema } from "../../../utils/validation";
import { useAdminResetUserPasswordMutation, useChangePasswordMutation } from "../../../services/api/user.api";
import ErrorPopup from "../errorPopup/errorPopup";
import GeneralSuccessPopup from "../generalSuccessPopup/generalSuccessPopup";
import Modal from "../modal/Modal";

function ChangePasswordPopup({
  onClose,
  targetUserGuid,
  onNewPassword,
}: IPopupWithoutProps & { targetUserGuid?: string; onNewPassword?: (password: string) => void }) {
  const isAdminReset = !!targetUserGuid;
  const isNewUser = !!onNewPassword && !targetUserGuid;

  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [repeatPasswordVisible, setRepeatPasswordVisible] = useState(false);
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);

  const [changePassword] = useChangePasswordMutation();
  const [adminResetUserPassword] = useAdminResetUserPasswordMutation();

  const formik = useFormik({
    initialValues: { currentPassword: "", password: "", repeatPassword: "" },
    validationSchema: isAdminReset || isNewUser ? passwordValidationSchema : changePasswordFormSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      if (isNewUser) {
        onNewPassword(values.password);
        onClose();
        return;
      }
      const result = isAdminReset
        ? await adminResetUserPassword({ targetUserGuid, newPassword: values.password })
        : await changePassword({ currentPassword: values.currentPassword, newPassword: values.password });
      if ("data" in result && result.data.success) {
        setOpenModalSuccess(true);
      } else {
        setOpenModalError(true);
      }
    },
  });

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  const toggleRepeatPasswordVisibility = () => {
    setRepeatPasswordVisible((prev) => !prev);
  };

  return (
    <article className={changePasswordPopupStyles.popup}>
      <h2 className={changePasswordPopupStyles.popup__title}> {t("CHANGE_PASSWORD_TITLE")} </h2>
      <form
        onSubmit={formik.handleSubmit}
        className={changePasswordPopupStyles.popup__form}
      >
        {!isAdminReset && !isNewUser && (
          <>
            <label
              htmlFor="currentPassword"
              className={changePasswordPopupStyles.popup__label}
            >
              {t("CURRENT_PASSWORD")}
            </label>
            <div className="inputContainer">
              <input
                type={currentPasswordVisible ? "text" : "password"}
                name="currentPassword"
                id="currentPassword"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${changePasswordPopupStyles.form__input} ${
                  formik.touched.currentPassword && formik.errors.currentPassword
                    ? changePasswordPopupStyles.form__input_invalid
                    : ""
                }`}
              />
              <button
                type="button"
                className={`passwordToggle ${currentPasswordVisible && "passwordToggle_visible"}`}
                onClick={() => setCurrentPasswordVisible((prev) => !prev)}
              />
            </div>
            {formik.touched.currentPassword && formik.errors.currentPassword && (
              <p className={changePasswordPopupStyles.popup__invalid_message}>
                {t(formik.errors.currentPassword)}
              </p>
            )}
          </>
        )}

        <label
          htmlFor="password"
          className={changePasswordPopupStyles.popup__label}
        >
          {t("NEW_PASSWORD")}
        </label>
        <div className="inputContainer">
          <input
            type={passwordVisible ? "text" : "password"}
            name="password"
            id="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`${changePasswordPopupStyles.form__input} ${
              formik.touched.password && formik.errors.password
                ? changePasswordPopupStyles.form__input_invalid
                : ""
            }`}
          />
          <button
            type="button"
            className={`passwordToggle ${
              passwordVisible &&
              "passwordToggle_visible"
            }`}
            onClick={togglePasswordVisibility}
          />
        </div>
        {formik.touched.password && formik.errors.password && (
          <p className={changePasswordPopupStyles.popup__invalid_message}>
            {t(formik.errors.password)}
          </p>
        )}

        <label
          htmlFor="repeatPassword"
          className={changePasswordPopupStyles.popup__label}
        >
           {t("CONFIRM_NEW_PASSWORD")}
        </label>
        <div className="inputContainer">
          <input
            type={repeatPasswordVisible ? "text" : "password"}
            name="repeatPassword"
            id="repeatPassword"
            value={t(formik.values.repeatPassword)}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`${changePasswordPopupStyles.form__input} ${
              formik.touched.repeatPassword && formik.errors.repeatPassword
                ? changePasswordPopupStyles.form__input_invalid
                : ""
            }`}
          />
          <button
            type="button"
            className={`passwordToggle ${
              repeatPasswordVisible &&
              "passwordToggle_visible"
            }`}
            onClick={toggleRepeatPasswordVisibility}
          />
        </div>
        {formik.touched.repeatPassword && formik.errors.repeatPassword && (
          <p className={changePasswordPopupStyles.popup__invalid_message}>
            {t(formik.errors.repeatPassword)}
          </p>
        )}

        <p className={changePasswordPopupStyles.popup__text}>
           {t("CHANGE_PASSWORD_INFO")}
        </p>

        <button
          type="submit"
          disabled={!formik.isValid || !formik.dirty}
          className={`${changePasswordPopupStyles.popup__submitBtn} ${
            !formik.isValid || !formik.dirty
              ? changePasswordPopupStyles.popup__submitBtn_disabled
              : ""
          }`}
        >
           {t("CHANGE_PASSWORD_SUBMIT_BUTTON")}
        </button>
      </form>
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
              onClose?.();
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => {
          setOpenModalError(false);
        }}>
          <ErrorPopup 
            onClose={() => {
              setOpenModalError(false);
            }} 
          />
        </Modal>
      )}

    </article>
  );
}

export default ChangePasswordPopup;
